import { useEffect, useMemo, useState } from 'react';
import type { ConsultaCifras, Indicador, ModoComparacion } from '../../api/ops/indicadores';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoVacia } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { hoyLocal } from '../../logica/fechasCortas';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { FichaIndicador } from './FichaIndicador';
import { Minigrafico } from './Grafico';
import { LoImportante, TarjetaCalor } from './LoImportante';
import type { PropsApartado } from './Indicadores';
import {
    A_LA_VISTA,
    agruparPorArea,
    agrupacionesPara,
    agrupacionValida,
    AYUDA_INDICADORES,
    SUBTITULO_INDICADORES,
    consultaDe,
    cambioDe,
    COMPARACIONES,
    importantes,
    indicadorDeCalor,
    mejorSiSube,
    rangoDeCalor,
    tonoDelCambio,
    diasEntre,
    errorDeRango,
    escribir,
    motivoIncompleto,
    PERIODOS,
    rangoDePeriodo,
    textoComparacion,
    textoRango,
    valorDibujable,
    visiblesConAmbito,
    type Filtro,
    type Periodo,
} from './reglas';
import { useCifras } from './useCifras';

/**
 * 7.2 «Indicadores»: filtros (periodo hasta 25 meses, sede, comparación —ninguna por defecto aquí—, agrupación) y el
 * catálogo de `listIndicators` agrupado por área, con 3 a la vista por área y «Ver los N de …». Cada tarjeta pide sus
 * cifras con `queryIndicator` y abre la ficha (`?indicador=`). Quien solo tiene «lo suyo» ve solo los indicadores con
 * `own_scope`. Arriba, «Lo importante» (`LoImportante`): las cifras de la tarjeta «Periodo» de `getDashboard`, en el
 * orden de los manifiestos. En cada área, el mapa de calor día × hora de su primer indicador por hora (`TarjetaCalor`).
 */
export function Catalogo(props: PropsApartado) {
    const { datos, busqueda, puedeExportar, irA, navegar, ruta } = props;
    const { ambito, puede } = usePermisos();
    const conPanel = puede('analitica.panel.ver');
    const [codigosPeriodo, setCodigosPeriodo] = useState<string[]>([]);
    useEffect(() => {
        if (!conPanel) {
            return;
        }
        let vivo = true;
        props.ops
            .getDashboard(props.negocio, 'last_30_days')
            .then((p) => {
                if (vivo) {
                    setCodigosPeriodo((p.cards.find((c) => c.code === 'period')?.items ?? []).map((i) => i.indicator));
                }
            })
            .catch(() => undefined); // sin el panel de inicio, sin «Lo importante»
        return () => {
            vivo = false;
        };
    }, [props.ops, props.negocio, conPanel]);
    const hoy = hoyLocal();
    const [filtro, setFiltro] = useState<Filtro>({ periodo: '30d', fechas: { desde: '', hasta: '' }, sede: '', comparar: 'none', agrupacion: null });
    const [area, setArea] = useState<string | null>(null);
    const [abiertos, setAbiertos] = useState<ReadonlySet<string>>(new Set());
    const soloLoSuyo = ambito('analitica.operativos.ver') === 'own';
    const visibles = useMemo(() => visiblesConAmbito(datos.catalogo, soloLoSuyo), [datos.catalogo, soloLoSuyo]);
    const grupos = useMemo(() => agruparPorArea(visibles), [visibles]);
    const rango = rangoDePeriodo(filtro.periodo, hoy, filtro.fechas);
    const errorFechas = filtro.periodo === 'fechas' ? errorDeRango(rango) : null;
    const consulta = consultaDe(filtro, hoy);
    const dias = errorFechas === null ? diasEntre(rango.desde, rango.hasta) : 30;
    const codigoAbierto = busqueda.get('indicador');
    const abierto = codigoAbierto === null ? null : (datos.catalogo.find((i) => i.code === codigoAbierto) ?? null);

    useCabecera({
        ayuda: AYUDA_INDICADORES,
        subtitulo: SUBTITULO_INDICADORES,
        acciones: puedeExportar ? [{ clave: 'exportar', texto: 'Exportar', icono: 'download', onClick: () => { irA('exportaciones', { nueva: '1' }); } }] : [],
    });

    const cambiar = (c: Partial<Filtro>) => {
        setFiltro((f) => ({ ...f, ...c }));
    };
    const abrir = (codigo: string | null) => {
        const p = new URLSearchParams(busqueda);
        if (codigo === null) {
            p.delete('indicador');
        } else {
            p.set('indicador', codigo);
        }
        const q = p.toString();
        navegar(`${ruta.split('?')[0] ?? ruta}${q === '' ? '' : `?${q}`}`, true);
    };
    const enVista = area === null ? grupos : grupos.filter((g) => g.area === area);
    const clave = area === null ? importantes(codigosPeriodo, visibles) : [];
    const linea = consulta === null ? '' : `${textoRango(rango.desde, rango.hasta)}${filtro.comparar === 'none' ? '' : `, frente a ${textoComparacion(filtro.comparar)}`}`;
    const fechasCalor = rangoDeCalor(rango.desde, rango.hasta);
    const consultaCalor = consulta === null ? null : { ...consulta, from: fechasCalor.desde, to: fechasCalor.hasta, granularity: 'hour' as const, compare: 'none' as const };

    return (
        <div className="rv-ind">
            <div className="rv-ind-filtros">
                <Desplegable etiqueta="Periodo" valor={filtro.periodo} opciones={PERIODOS.map((p) => ({ valor: p.valor, texto: p.texto }))} onChange={(v) => { cambiar({ periodo: v as Periodo }); }} />
                {filtro.periodo === 'fechas' && (
                    <>
                        <Fecha etiqueta="Desde" valor={filtro.fechas.desde} max={hoy} onChange={(v) => { cambiar({ fechas: { ...filtro.fechas, desde: v } }); }} />
                        <Fecha etiqueta="Hasta" valor={filtro.fechas.hasta} max={hoy} error={filtro.fechas.desde !== '' && filtro.fechas.hasta !== '' ? errorFechas : null} onChange={(v) => { cambiar({ fechas: { ...filtro.fechas, hasta: v } }); }} />
                    </>
                )}
                {datos.sedes.length > 1 && (
                    <Desplegable etiqueta="Sede" valor={filtro.sede} opciones={[{ valor: '', texto: 'Todas las sedes' }, ...datos.sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} onChange={(v) => { cambiar({ sede: v }); }} />
                )}
                <Desplegable etiqueta="Comparar con" valor={filtro.comparar} opciones={COMPARACIONES.map((c) => ({ valor: c.valor, texto: c.texto }))} onChange={(v) => { cambiar({ comparar: v as ModoComparacion }); }} />
                <Desplegable
                    etiqueta="Agrupar por"
                    valor={agrupacionValida(filtro.agrupacion, dias)}
                    opciones={agrupacionesPara(dias).map((g) => ({ valor: g.valor, texto: g.texto }))}
                    onChange={(v) => { cambiar({ agrupacion: v as Filtro['agrupacion'] }); }}
                />
            </div>
            {grupos.length > 1 && (
                <Filtros
                    etiqueta="Áreas"
                    items={[{ etiqueta: 'Todos', cifra: visibles.length }, ...grupos.map((g) => ({ etiqueta: g.nombre }))]}
                    valor={area === null ? 0 : grupos.findIndex((g) => g.area === area) + 1}
                    onCambio={(i) => { setArea(i === 0 ? null : (grupos[i - 1]?.area ?? null)); }}
                />
            )}
            {soloLoSuyo && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>Con tu rol ves solo tus cifras.</span>
                </p>
            )}
            {consulta !== null && clave.length === 0 && <p className="rv-ind-linea">{linea}</p>}
            {consulta !== null && clave.length > 0 && <LoImportante {...props} lista={clave} consulta={consulta} filtro={filtro} linea={linea} onAbrir={abrir} />}
            {grupos.length === 0 ? (
                <EstadoVacia seccion="indicadores" />
            ) : (
                enVista.map((g) => {
                    const todos = abiertos.has(g.area);
                    const lista = todos ? g.indicadores : g.indicadores.slice(0, A_LA_VISTA);
                    const deCalor = indicadorDeCalor(g.indicadores);
                    return (
                        <section key={g.area} className="rv-ind-grupo" aria-label={g.nombre}>
                            <h2>
                                <Icono nombre={g.icono} />
                                {g.nombre}
                            </h2>
                            {deCalor !== null && <TarjetaCalor {...props} indicador={deCalor} consulta={consultaCalor} recortado={fechasCalor.recortado} onAbrir={() => { abrir(deCalor.code); }} />}
                            <div className="rv-ind-tarjetas">
                                {lista.map((i) => (
                                    <Tarjeta key={i.code} {...props} indicador={i} consulta={consulta} onAbrir={() => { abrir(i.code); }} />
                                ))}
                            </div>
                            {g.indicadores.length > A_LA_VISTA && (
                                <button
                                    type="button"
                                    className="rv-ind-mas"
                                    onClick={() => {
                                        setAbiertos((a) => {
                                            const n = new Set(a);
                                            if (todos) {
                                                n.delete(g.area);
                                            } else {
                                                n.add(g.area);
                                            }
                                            return n;
                                        });
                                    }}
                                >
                                    {todos ? 'Ver menos' : `Ver los ${String(g.indicadores.length)} de ${g.nombre}`}
                                    <Icono nombre={todos ? 'chevron-up' : 'chevron-down'} />
                                </button>
                            )}
                        </section>
                    );
                })
            )}
            <FichaIndicador {...props} indicador={abierto} filtro={filtro} onCerrar={() => { abrir(null); }} />
        </div>
    );
}

function Tarjeta({ ops, negocio, indicador, consulta, onAbrir }: PropsApartado & { indicador: Indicador; consulta: ConsultaCifras | null; onAbrir: () => void }) {
    const { cifras, error } = useCifras(ops, negocio, indicador.code, consulta);
    const serie = cifras?.series.find((s) => (s.dimension_value ?? null) === null) ?? cifras?.series[0];
    const unidad = cifras?.unit ?? indicador.unit;
    const e = escribir(unidad, serie?.total);
    const cambio = cifras === null ? null : cambioDe(unidad, cifras.comparison, serie ?? null);
    const incompleto = serie?.points.some((p) => motivoIncompleto(p) !== null) ?? false;
    return (
        <button type="button" className="rv-ind-tarjeta" onClick={onAbrir} aria-label={`${indicador.name}: abrir la ficha`}>
            <span className="rv-ind-tarjeta__titulo">
                {indicador.name}
                <Icono nombre="chevron-right" />
            </span>
            {error !== null ? (
                <span className="rv-ind-tarjeta__nota">No se han podido leer las cifras.</span>
            ) : cifras === null ? (
                <span className="rv-ind-tarjeta__nota">Cargando…</span>
            ) : (
                <>
                    <span className="rv-ind-tarjeta__cifra">
                        <strong>{e.cifra}</strong>
                        {e.unidad !== '' && <small>{e.unidad}</small>}
                        {cambio !== null && <em className={`rv-ind-cambio rv-ind-cambio--${tonoDelCambio(cambio.sentido, mejorSiSube(indicador))}`}>{cambio.texto}</em>}
                    </span>
                    <Minigrafico valores={(serie?.points ?? []).map(valorDibujable)} />
                    {incompleto && <span className="rv-ind-tarjeta__nota">Falta un día por completar.</span>}
                </>
            )}
        </button>
    );
}
